import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const cookieName = 'jetscanner_cookie_consent';
  const cookieExpiry = 365; // days

  useEffect(() => {
    // Check if user has already made a choice
    if (!hasConsent()) {
      showBanner();
    }
  }, []);

  const hasConsent = () => {
    return getCookie(cookieName) !== null;
  };

  const showBanner = () => {
    setIsVisible(true);
    // Add animation delay
    setTimeout(() => {
      setIsAnimating(true);
    }, 100);
  };

  const hideBanner = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  const acceptCookies = () => {
    setCookie(cookieName, 'accepted', cookieExpiry);
    // Save all preferences as accepted
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    localStorage.setItem('jetscanner_cookie_preferences', JSON.stringify(allAccepted));
    hideBanner();
    initializeAnalytics(allAccepted);
  };

  const declineCookies = () => {
    setCookie(cookieName, 'declined', cookieExpiry);
    // Save only necessary cookies
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    localStorage.setItem('jetscanner_cookie_preferences', JSON.stringify(onlyNecessary));
    hideBanner();
  };

  const initializeAnalytics = (preferences) => {
    if (typeof window.gtag !== 'function') return;

    if (preferences.analytics) {
      window.gtag('consent', 'update', { 'analytics_storage': 'granted' });
    }

    if (preferences.marketing) {
      window.gtag('consent', 'update', { 'ad_storage': 'granted' });
    }
  };

  const setCookie = (name, value, days) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "expires=" + date.toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/;SameSite=Lax";
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)] p-5 z-[999999] border-t-3 border-[#ffd200] transition-all duration-300 ${
        isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'
      }`}
    >
      <div className="max-w-[1200px] mx-auto flex items-center justify-between flex-wrap gap-5">
        <div className="flex-1 min-w-[300px]">
          <h4 className="m-0 mb-2 text-lg font-semibold text-[#111236] font-['Bahnschrift',sans-serif]">
            We Value Your Privacy
          </h4>
          <p className="m-0 text-sm text-[#666] leading-relaxed">
            We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
            By clicking "Accept", you consent to our use of cookies.{' '}
            <Link to="/cookie-policy" target="_blank" className="text-[#111236] underline hover:text-[#ffd200]">
              Learn more about our Cookie Policy
            </Link>
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button 
            onClick={declineCookies}
            className="px-6 py-2.5 border-2 border-[#b9b9b9] rounded-md text-sm font-semibold cursor-pointer transition-all duration-300 font-['Bahnschrift',sans-serif] whitespace-nowrap bg-[#f3f3f3] text-[#666] hover:bg-[#e0e0e0] hover:text-[#333]"
          >
            Decline
          </button>
          <button 
            onClick={acceptCookies}
            className="px-6 py-2.5 border-none rounded-md text-sm font-semibold cursor-pointer transition-all duration-300 font-['Bahnschrift',sans-serif] whitespace-nowrap bg-gradient-to-br from-[#ffeb00] to-[#ffd200] text-black hover:from-[#ffd200] hover:to-[#ffb900] hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(255,210,0,0.3)]"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
